import { ModuleIntro } from "@/components/ModuleIntro";
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Layout } from "@/components/Layout";
import { LanguageToggle } from "@/components/LanguageToggle";
import { usePatientMode } from "@/lib/patientMode";
import { NeedStep } from "@/components/NeedStep";
import { CoupleResults } from "@/components/couple/CoupleResults";
import { NEEDS, SATISFACTION_LABELS } from "@/data/needs";
import { COUPLE_ITEMS, COUPLE_T } from "@/data/couple";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
const mergedNeed = (i) => ({ key: NEEDS[i].key, name: NEEDS[i].name, short: NEEDS[i].short, items: COUPLE_ITEMS[NEEDS[i].key] });

const Primary = ({ children, onClick, testid, disabled }) => (
  <button onClick={onClick} data-testid={testid} disabled={disabled}
    className={`px-8 py-3.5 rounded-full font-medium shadow-sm transition-all ${disabled ? "bg-[var(--beige)] text-[var(--ink-soft)] cursor-not-allowed" : "bg-[var(--gold)] text-white hover:brightness-105"}`}>
    {children}
  </button>
);
const Ghost = ({ children, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} className="px-6 py-3 rounded-full border border-[var(--gold)] text-[var(--gold)] font-medium hover:bg-[var(--gold)] hover:text-white transition-colors">
    {children}
  </button>
);

// --- Étape d'estimation 0–20 (auto ou de l'autre) ---
const EstimateStep = ({ t, lang, title, sub, showDef, values, onChange, onPrev, onNext, ctaTestid }) => {
  const complete = NEEDS.every((n) => Number.isFinite(values[n.key]));
  return (
    <div className="animate-fade-up">
      <h2 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)] mb-2 text-center leading-tight">{title}</h2>
      <p className="text-sm text-[var(--ink-soft)] text-center max-w-xl mx-auto mb-8">{sub}</p>
      <div className="space-y-4">
        {NEEDS.map((need) => {
          const v = values[need.key];
          const set = Number.isFinite(v);
          const commit = (e) => onChange(need.key, Number(e.target.value));
          return (
            <div key={need.key} className="rounded-2xl border border-[var(--border)] bg-white/70 p-5" data-testid={`couple-est-${need.key}`}>
              <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-2">{need.name[lang]}</h3>
              {showDef && <p className="text-sm text-[var(--ink)] leading-relaxed mb-3">{need.definition[lang]}</p>}
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-sm text-[var(--ink-soft)]">{t.lowSat}</span>
                <span className={`font-serif-title text-3xl ${set ? "text-[var(--gold)]" : "text-[var(--ink-soft)]"}`} data-testid={`couple-est-${need.key}-value`}>
                  {set ? v : "—"}<span className="text-base"> / 20</span>
                </span>
                <span className="text-sm text-[var(--ink-soft)]">{t.highSat}</span>
              </div>
              <input type="range" min={0} max={20} step={1} value={set ? v : 10}
                onChange={commit} onPointerUp={commit} onKeyUp={commit} onClick={commit}
                data-testid={`couple-est-${need.key}-slider`}
                className={`w-full h-2 rounded-full appearance-none cursor-pointer accent-[var(--gold)] ${set ? "bg-[var(--gold-light)]" : "bg-[var(--beige)]"}`} />
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between mt-8 no-print">
        <Ghost onClick={onPrev} testid="couple-btn-prev">{t.prev}</Ghost>
        <Primary onClick={onNext} disabled={!complete} testid={ctaTestid}>{t.next}</Primary>
      </div>
      {!complete && <p className="text-center text-xs text-[var(--ink-soft)] mt-3 no-print">{t.completeSliders}</p>}
    </div>
  );
};

// --- Parcours d'un partenaire : intro -> 10 besoins -> auto-estimation -> estimation de l'autre ---
const PartnerFlow = ({ lang, t, initialName, onDone }) => {
  const [sub, setSub] = useState("intro");
  const [name, setName] = useState(initialName || "");
  const [needIndex, setNeedIndex] = useState(0);
  const [measured, setMeasured] = useState({});
  const [selfEst, setSelfEst] = useState({});
  const [otherEst, setOtherEst] = useState({});
  const scale = SATISFACTION_LABELS[lang];

  const setAnswer = (key, idx, val) => setMeasured((prev) => {
    const arr = [...(prev[key] || new Array(5).fill(undefined))];
    arr[idx] = val;
    return { ...prev, [key]: arr };
  });

  if (sub === "intro") {
    return (
      <div className="animate-fade-up">
        <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 sm:p-8 mb-6 text-[var(--ink)] leading-relaxed space-y-3">
          <p>{t.intro1}</p>
          <p>{t.intro2}</p>
          <p><strong>{t.intro3}</strong></p>
        </div>
        <div className="rounded-2xl bg-white/70 border border-[var(--border)] p-5 mb-6">
          <label htmlFor="couple-name" className="block text-sm text-[var(--ink)] mb-2 font-medium">{t.nameLabel}</label>
          <input id="couple-name" data-testid="couple-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder={t.namePlaceholder}
            className="w-full rounded-xl border border-[var(--border)] bg-[var(--sand)] p-3 focus:outline-none focus:border-[var(--gold)]" />
        </div>
        <div className="text-center">
          <Primary onClick={() => { setSub("needs"); scrollTop(); }} testid="couple-btn-begin">{t.begin}</Primary>
        </div>
      </div>
    );
  }

  if (sub === "needs") {
    const need = mergedNeed(needIndex);
    return (
      <NeedStep
        need={need}
        indexNumber={needIndex + 1}
        total={NEEDS.length}
        values={measured[need.key]}
        onChange={(i, v) => setAnswer(need.key, i, v)}
        onPrev={() => { if (needIndex > 0) { setNeedIndex((x) => x - 1); } else { setSub("intro"); } scrollTop(); }}
        onNext={() => { if (needIndex < NEEDS.length - 1) { setNeedIndex((x) => x + 1); } else { setSub("self"); } scrollTop(); }}
        lang={lang}
        t={t}
        scale={scale}
      />
    );
  }

  if (sub === "self") {
    return (
      <EstimateStep t={t} lang={lang} title={t.selfTitle} sub={t.selfSub} showDef
        values={selfEst} onChange={(k, v) => setSelfEst((p) => ({ ...p, [k]: v }))}
        onPrev={() => { setSub("needs"); setNeedIndex(NEEDS.length - 1); scrollTop(); }}
        onNext={() => { setSub("other"); scrollTop(); }} ctaTestid="couple-btn-self-next" />
    );
  }

  // other
  return (
    <EstimateStep t={t} lang={lang} title={t.otherTitle} sub={t.otherSub} showDef={false}
      values={otherEst} onChange={(k, v) => setOtherEst((p) => ({ ...p, [k]: v }))}
      onPrev={() => { setSub("self"); scrollTop(); }}
      onNext={() => onDone({ name, measured, selfEst, otherEst })} ctaTestid="couple-btn-other-next" />
  );
};

const TopBar = ({ lang, setLang, t }) => {
  const { patientMode } = usePatientMode();
  return (
    <div className="flex items-center justify-between mb-6 no-print">
      {patientMode ? <span /> : <Link to="/" data-testid="couple-home-link" className="text-sm text-[var(--ink-soft)] hover:text-[var(--gold)] transition-colors">{t.allModules}</Link>}
      <LanguageToggle lang={lang} onChange={setLang} />
    </div>
  );
};

export default function CoupleModule({ initialLang }) {
  const { patientMode } = usePatientMode();
  const [lang, setLang] = useState(initialLang || "fr");
  const [phase, setPhase] = useState("mode");
  const [p1, setP1] = useState(null);
  const [p2, setP2] = useState(null);
  const [code, setCode] = useState("");
  const [role, setRole] = useState("p1");
  const [joinInput, setJoinInput] = useState("");
  const [serverData, setServerData] = useState(null);
  const t = COUPLE_T[lang];

  // Auto-join via ?code= (deuxième appareil)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const c = params.get("code");
    if (c) { setCode(c.toUpperCase()); setRole("p2"); setPhase("td_fill"); }
  }, []);

  const reset = () => { setP1(null); setP2(null); setCode(""); setRole("p1"); setJoinInput(""); setServerData(null); setPhase("mode"); scrollTop(); };

  // --- Two-device polling ---
  const poll = useCallback(async (c) => {
    try {
      const data = await axios.get(`${API}/besoins-couple/seances/${c}`).then((r) => r.data);
      setServerData(data);
      if (data.both_submitted) { setP1(data.p1); setP2(data.p2); setPhase("td_results"); }
    } catch { /* réseau */ }
  }, []);

  useEffect(() => {
    if (phase !== "td_wait" || !code) return;
    poll(code);
    const id = setInterval(() => poll(code), 3000);
    return () => clearInterval(id);
  }, [phase, code, poll]);

  const createSeance = async () => {
    try {
      const data = await axios.post(`${API}/besoins-couple/seances?lang=${lang}`).then((r) => r.data);
      setCode(data.code); setRole("p1"); setPhase("td_code"); scrollTop();
    } catch { toast.error(t.invalidCode); }
  };
  const joinSeance = async () => {
    const c = joinInput.trim().toUpperCase();
    if (!c) return;
    try {
      const data = await axios.get(`${API}/besoins-couple/seances/${c}`).then((r) => r.data);
      if (!data.found) { toast.error(t.invalidCode); return; }
      setCode(c); setRole("p2"); setPhase("td_fill"); scrollTop();
    } catch { toast.error(t.invalidCode); }
  };
  const submitToServer = async (partnerData) => {
    try {
      await axios.post(`${API}/besoins-couple/seances/${code}/submit`, { role, ...partnerData });
    } catch { /* on continue même si le réseau échoue */ }
    setPhase("td_wait"); scrollTop();
  };

  const render = () => {
    switch (phase) {
      case "mode":
        return (
          <div className="animate-fade-up">
            <img src="/modules/besoins-couple.jpg" alt="" className="w-full h-52 sm:h-64 object-cover rounded-3xl mb-8" data-testid="couple-welcome-hero" />
            <div className="text-center mb-8">
              <h1 className="font-serif-title text-4xl sm:text-5xl text-[var(--gold)] mb-3">{t.title}</h1>
              <p className="text-[var(--ink-soft)] max-w-md mx-auto leading-relaxed">{t.subtitle}</p>
            </div>
            <ModuleIntro moduleKey="besoins-couple" lang={lang} className="mb-8" />
            <h2 className="font-serif-title text-2xl text-[var(--gold)] text-center mb-6">{t.modeTitle}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <button onClick={() => { setPhase("sd_p1"); scrollTop(); }} data-testid="couple-mode-same" className="rounded-3xl bg-white/70 border border-[var(--border)] p-7 text-left hover:shadow-md transition-all">
                <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-2">{t.modeSame}</h3>
                <p className="text-sm text-[var(--ink-soft)]">{t.modeSameDesc}</p>
              </button>
              <button onClick={() => { setPhase("td_home"); scrollTop(); }} data-testid="couple-mode-two" className="rounded-3xl bg-white/70 border border-[var(--border)] p-7 text-left hover:shadow-md transition-all">
                <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-2">{t.modeTwo}</h3>
                <p className="text-sm text-[var(--ink-soft)]">{t.modeTwoDesc}</p>
              </button>
            </div>
            <img src="/couple/besoins-inside.jpg" alt="" className="w-full h-40 sm:h-52 object-cover rounded-3xl mt-8" data-testid="couple-mode-photo" />
          </div>
        );

      // ---- Même appareil ----
      case "sd_p1":
        return <PartnerFlow lang={lang} t={t} onDone={(d) => { setP1(d); setPhase("sd_handoff"); scrollTop(); }} />;
      case "sd_handoff":
        return (
          <div className="animate-fade-up text-center">
            <h2 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)] mb-4">{t.handoffTitle}</h2>
            <p className="text-[var(--ink)] leading-relaxed max-w-lg mx-auto mb-8">{t.handoffDesc}</p>
            <Primary onClick={() => { setPhase("sd_p2"); scrollTop(); }} testid="couple-handoff-btn">{t.handoffBtn}</Primary>
          </div>
        );
      case "sd_p2":
        return <PartnerFlow lang={lang} t={t} onDone={(d) => { setP2(d); setPhase("sd_results"); scrollTop(); }} />;
      case "sd_results":
        return <CoupleResults p1={p1} p2={p2} lang={lang} onRestart={reset} persist />;

      // ---- Deux appareils ----
      case "td_home":
        return (
          <div className="animate-fade-up">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-7 text-center">
                <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-4">{t.createTitle}</h3>
                <Primary onClick={createSeance} testid="couple-create-btn">{t.createBtn}</Primary>
              </div>
              <div className="rounded-3xl bg-white/70 border border-[var(--border)] p-7 text-center">
                <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-4">{t.joinTitle}</h3>
                <input value={joinInput} onChange={(e) => setJoinInput(e.target.value)} placeholder={t.joinPlaceholder} data-testid="couple-join-input"
                  className="w-full rounded-xl border border-[var(--border)] bg-[var(--sand)] p-3 mb-3 text-center font-mono tracking-widest uppercase focus:outline-none focus:border-[var(--gold)]" />
                <Primary onClick={joinSeance} testid="couple-join-btn">{t.joinBtn}</Primary>
              </div>
            </div>
          </div>
        );
      case "td_code": {
        const joinUrl = `${window.location.origin}${patientMode ? "/p/besoins-couple" : "/besoins-couple"}?code=${code}`;
        return (
          <div className="animate-fade-up text-center">
            <h2 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)] mb-4">{t.codeLabel}</h2>
            <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-8 mb-6">
              <p className="text-sm text-[var(--ink-soft)] mb-3">{t.shareCode}</p>
              <div className="font-mono text-5xl tracking-[0.4em] text-[var(--gold)] font-bold my-4" data-testid="couple-code-value">{code}</div>
              <div className="flex justify-center my-4" data-testid="couple-qr">
                <div className="bg-white p-3 rounded-2xl border border-[var(--border)]">
                  <QRCodeSVG value={joinUrl} size={148} bgColor="#ffffff" fgColor="#4A3B36" level="M" />
                </div>
              </div>
              <button onClick={() => { navigator.clipboard.writeText(code).then(() => toast.success("OK")).catch(() => {}); }} className="text-sm text-[var(--ink-soft)] hover:text-[var(--gold)]">{t.codeLabel}</button>
            </div>
            <Primary onClick={() => { setPhase("td_fill"); scrollTop(); }} testid="couple-code-fill">{t.continue}</Primary>
          </div>
        );
      }
      case "td_fill":
        return <PartnerFlow lang={lang} t={t} onDone={submitToServer} />;
      case "td_wait":
        return (
          <div className="animate-fade-up text-center">
            <h2 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)] mb-4">{t.waiting}</h2>
            <p className="text-[var(--ink)] leading-relaxed max-w-lg mx-auto mb-6">{t.waitingDesc}</p>
            <div className="font-mono text-2xl tracking-[0.3em] text-[var(--gold)] mb-6">{code}</div>
            <Ghost onClick={() => poll(code)} testid="couple-refresh-btn">{t.refresh}</Ghost>
          </div>
        );
      case "td_results":
        return <CoupleResults p1={p1} p2={p2} lang={lang} onRestart={reset} />;

      default:
        return null;
    }
  };

  return (
    <Layout>
      <TopBar lang={lang} setLang={setLang} t={t} />
      {render()}
    </Layout>
  );
}
